import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n';
import { SEOHead } from '@/components/SEOHead';
import { productSchema, breadcrumbSchema, lowestPrice } from '@/lib/seo';
import {
  HardDrive, Globe, Shield, Zap, Check, ExternalLink,
  Database, Cloud, Mail, Lock, LifeBuoy, Server, TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const nvmePlans = [
  {
    name: 'Web NVMe Special',
    storage: '5 GB NVMe',
    domainsIncluded: 2,
    domainsAssignable: null,
    emails: 30,
    databases: 10,
    price: '25,00',
    period: 'year',
    pid: 316,
    hasNote: true,
    features: ['PHP 7', 'Memory Limit 256M', '20 domains assignable', 'Application Manager', 'Flat Traffic', 'DDoS Protection'],
  },
  {
    name: 'Web NVMe Starter',
    storage: '5 GB NVMe',
    domainsIncluded: null,
    domainsAssignable: 15,
    emails: 20,
    databases: 10,
    price: '2,95',
    period: 'month',
    pid: 314,
    popular: true,
    features: ['PHP 7', 'Memory Limit 256M', 'Application Manager', 'Website Building Kit', 'Flat Traffic', 'DDoS Protection'],
  },
  {
    name: 'Web NVMe Extreme',
    storage: '10 GB NVMe',
    domainsIncluded: null,
    domainsAssignable: 50,
    emails: 100,
    databases: 50,
    price: '3,95',
    period: 'month',
    pid: 315,
    features: ['PHP 7', 'Memory Limit 256M', 'Application Manager', 'Website Building Kit', 'Flat Traffic', 'DDoS Protection'],
  },
];

const fromMonthly = lowestPrice(nvmePlans.filter((p) => p.period === 'month').map((p) => p.price));
const fromYearly  = lowestPrice(nvmePlans.filter((p) => p.period === 'year').map((p) => p.price));

const includedFeatureKeys = ['ssl', 'ddos', 'email', 'db', 'plesk', 'support'];
const includedFeatureIcons = { ssl: Lock, ddos: Shield, email: Mail, db: Database, plesk: Server, support: LifeBuoy };

const HostingCard = ({ plan, t }) => (
  <Card className={`h-full relative flex flex-col ${plan.popular ? 'border-primary/50 bg-primary/5' : ''}`}>
    {plan.popular && (
      <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">{t('common.popular')}</Badge>
    )}
    <CardHeader>
      <CardTitle className="text-base">{plan.name}</CardTitle>
      <CardDescription>{plan.storage}</CardDescription>
    </CardHeader>
    <CardContent className="flex flex-col flex-1">
      <div className="space-y-2 mb-5 text-sm">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-primary shrink-0" />
          <span>
            {plan.domainsIncluded
              ? `${plan.domainsIncluded}x .DE ${t('common.domainsIncluded')}*`
              : `${plan.domainsAssignable} ${t('common.domainsAssignable')}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-blue-400 shrink-0" />
          <span>{plan.emails} {t('common.emailAccounts')}</span>
        </div>
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-green-400 shrink-0" />
          <span>{plan.databases} {t('common.databases')}</span>
        </div>
      </div>
      <ul className="space-y-1.5 mb-5 flex-1">
        {plan.features.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="w-4 h-4 text-green-500 shrink-0" />
            {feature}
          </li>
        ))}
      </ul>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-3xl font-black text-primary">€{plan.price}</span>
        <span className="text-muted-foreground text-sm">
          {plan.period === 'year' ? t('common.perYear') : t('common.perMonth')}
        </span>
      </div>
      {plan.hasNote && (
        <p className="text-xs text-muted-foreground mb-4 leading-snug">{t('common.domainsNote')}</p>
      )}
      {!plan.hasNote && <div className="mb-4" />}
      <a href={`https://deinserverhost.de/store/cart.php?a=add&pid=${plan.pid}`} target="_blank" rel="noopener noreferrer">
        <Button className="w-full bg-primary hover:bg-primary/90" data-testid={`order-nvme-${plan.pid}`}>
          {t('common.orderNow')} <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </a>
    </CardContent>
  </Card>
);

export default function WebhostingNVMePage() {
  const { t } = useTranslation();

  const stats = [
    { valueKey: 'nvme_stat1_value', labelKey: 'nvme_stat1_label', subKey: 'nvme_stat1_sub' },
    { valueKey: 'nvme_stat2_value', labelKey: 'nvme_stat2_label', subKey: 'nvme_stat2_sub' },
    { valueKey: 'nvme_stat3_value', labelKey: 'nvme_stat3_label', subKey: 'nvme_stat3_sub' },
  ];

  return (
    <Layout>
      <SEOHead
        title="NVMe Web Hosting – Ultra-Fast SSD Storage"
        description={`NVMe web hosting for maximum speed with free SSL, email, PHP & MySQL and DDoS protection – from €${fromMonthly}/month or €${fromYearly}/year.`}
        canonical="/webhosting/nvme"
        schema={[
          productSchema({
            name: 'NVMe Web Hosting',
            description: 'NVMe web hosting with free SSL, email accounts, PHP & MySQL, Plesk and DDoS protection.',
            path: '/webhosting/nvme',
            offers: nvmePlans.map((plan) => ({ name: plan.name, price: plan.price, period: plan.period })),
          }),
          breadcrumbSchema([{ name: 'Web Hosting', path: '/webhosting' }, { name: 'NVMe Web Hosting', path: '/webhosting/nvme' }]),
        ]}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Hero */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              <Badge className="mb-4 bg-primary/20 text-primary border border-primary/30">
                {t('webhosting.nvme_badge')}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                {t('webhosting.nvme_title1')}<br /><span className="text-gradient">{t('webhosting.nvme_title2')}</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-6">{t('webhosting.nvme_desc')}</p>
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: TrendingUp, key: 'nvme_badge_speed' },
                  { icon: Shield,     key: 'nvme_badge_ddos' },
                  { icon: Lock,       key: 'nvme_badge_ssl' },
                ].map(({ icon: Icon, key }) => (
                  <div key={key} className="flex items-center gap-2 px-3 py-1.5 rounded-full glass text-sm">
                    <Icon className="w-4 h-4 text-primary" />{t(`webhosting.${key}`)}
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Link to="/webhosting/ssd">
                  <Button variant="outline" size="sm" className="border-primary/30 hover:border-primary">
                    <HardDrive className="w-4 h-4 mr-2" />{t('webhosting.nvme_link_ssd')}
                  </Button>
                </Link>
                <Link to="/webhosting/nextcloud">
                  <Button variant="outline" size="sm" className="border-primary/30 hover:border-primary">
                    <Cloud className="w-4 h-4 mr-2" />Nextcloud
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="flex justify-center"
            >
              <img src="/images/penguin/penguin_run_ssd.svg" alt="DeinServerHost Mascot" title="DeinServerHost Mascot" className="w-64 md:w-80 drop-shadow-2xl" />
            </motion.div>
          </div>

          {/* Plans */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-16">
            <h2 className="text-2xl font-bold text-center mb-8">{t('webhosting.nvme_plansTitle')}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {nvmePlans.map((plan) => (
                <HostingCard key={plan.name} plan={plan} t={t} />
              ))}
            </div>
          </motion.div>

          {/* Speed Stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="p-8 glass rounded-2xl border border-border mb-16">
            <h2 className="text-2xl font-bold text-center mb-2">{t('webhosting.nvme_statsTitle')}</h2>
            <p className="text-center text-muted-foreground text-sm mb-8">{t('webhosting.nvme_statsSubtitle')}</p>
            <div className="grid md:grid-cols-3 gap-6">
              {stats.map(({ valueKey, labelKey, subKey }) => (
                <div key={valueKey} className="text-center">
                  <div className="text-5xl font-black text-primary mb-2">{t(`webhosting.${valueKey}`)}</div>
                  <div className="font-semibold mb-1">{t(`webhosting.${labelKey}`)}</div>
                  <div className="text-sm text-muted-foreground">{t(`webhosting.${subKey}`)}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Included Features */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mb-16">
            <h2 className="text-2xl font-bold text-center mb-3">{t('webhosting.nvme_includedTitle')}</h2>
            <p className="text-center text-muted-foreground mb-8">{t('webhosting.nvme_includedSubtitle')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {includedFeatureKeys.map((key) => {
                const Icon = includedFeatureIcons[key];
                return (
                  <div key={key} className="flex items-start gap-4 p-4 glass rounded-xl border border-border">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{t(`webhosting.feat_${key}_label`)}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{t(`webhosting.feat_${key}_desc`)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="text-center p-8 glass rounded-2xl">
            <h2 className="text-2xl font-bold mb-4">{t('webhosting.nvme_ctaTitle')}</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">{t('webhosting.nvme_ctaDesc')}</p>
            <Link to="/product-finder">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover">
                {t('common.startProductFinder')}
              </Button>
            </Link>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
